import templateUrl from "/templates/header.html?url";
import { loadRemoteElement, getDefaultElementTargetSelector } from "../core";
import logoUrl from "$assets/united/images/logo.svg";
import focusDir from "../stores/directionStore";
import { isMobileActive, isSearchActive } from "../stores/bodyStates";
import ReactiveClassStore from "../stores/ReactiveClassStore";
import { Logger } from "$lib/core/js/util/logger";

/** @type {number} */
export const mobileWidth = 1100;
/** @type {number} Supposedly, a problem could occur if this number is changed? Unclear what exactly though. Remnant of old comment for a magic number. */
export const searchBarTransitionDuration = 600;

export class Header {
	/** @type {string} */
	#selector = getDefaultElementTargetSelector("Header", true);
	/** @type {HTMLElement?} */
	#target = null;
	/** @type {Logger} */
	#logger = new Logger("Header");

  // Element Bindings

  /** @type {HTMLInputElement?} */
  searchInput = null;
  /** @type {HTMLButtonElement?} */
  searchOpenButton = null;
  /** @type {HTMLButtonElement?} */
  searchCloseButton = null;
  /** @type {HTMLButtonElement?} */
  mobileNavButton = null;
  /** @type {HTMLAnchorElement?} */
  brandAnchor = null;
  /** @type {HTMLAnchorElement?} */
  skipToNav = null;
  /** @type {HTMLAnchorElement?} */
  siteBrandingName = null;
  /** @type {HTMLElement?} */
  headerElement = null;

	// External Bindings' HTML Elements

	/** @type {HTMLElement?} Represents `primaryMenu` from Svelte Header. */
	primaryNav = null;
	/** @type {HTMLElement?} Represents `mobileMenu` from Svelte Header. */
  mobileNav = null;
	/** @type {HTMLInputElement?} */
  menu11 = null;

	// External Bindings

  /** @type {number} */
  windowWidth = 0;

	// Store Values

	/** @type {boolean} */
	#isSearchActive = false;
	/** @type {boolean} */
	#isMobileActive = false;
	/** @type {import("../public").DirectionAxis1} */
	#focusDir = "";
	/** @type {boolean} */
	#isNoSearch = false;
	/** @type {ReactiveClassStore?} */
	#noSearchStore = null;
	
	// Render State
	
  /** @type {boolean} If true, the mobile nav button should be displayed in place of the primary navigation menu. */
  isMobile = false;
	
	/**
	 * @param {{ selector?: string, target?: HTMLElement }} options
	 */
	constructor(options = {}) {
		this.#selector = options.selector ?? this.#selector;
		this.#target = options.target ?? null;
	}

	/**
	 * @returns {string}
	 */
	get selector() {
		return this.#selector;
	}

	/**
	 * @returns {HTMLElement?}
	 */
	get target() {
		if (!this.#target) {
			this.#target = document.querySelector(this.#selector);
		}
		return this.#target;
	}

	/**
	 * @param {HTMLElement} value
	 */
	set target(value) {
		this.#target = value;
	}

	get isNoSearch() {
		return this.#isNoSearch;
	}

	/**
	 * @param {boolean} value
	 */
	set isNoSearch(value) {
		this.#isNoSearch = value;
		this.onNoSearchUpdated(this.#isNoSearch);
	}

	/**
	 * @param {boolean} value
	 */
	onNoSearchUpdated(value) {
		const s = document.getElementById("header_search");
		if (value) {
			if (s) {
				s.style.display = "none";
			}
			if (this.searchOpenButton) {
				this.searchOpenButton.disabled = true;
				this.searchOpenButton.classList.add("invisible");
				this.searchOpenButton.setAttribute("aria-hidden", "true");
			}
		} else {
			if (s) {
				s.style.display = "";
			}
			if (this.searchOpenButton) {
				this.searchOpenButton.disabled = false;
				this.searchOpenButton.classList.remove("invisible");
				this.searchOpenButton.setAttribute("aria-hidden", "false");
			}
		}
	}

	/**
	 * @param {MouseEvent} ev 
	 */
  onClickOpenSearchButton(ev) {
		ev.preventDefault();
		isSearchActive.set(true);
    setTimeout(() => {
      this.searchInput?.focus();
    }, searchBarTransitionDuration);
  }

  onBlurOpenSearchButton() {
    if (this.#focusDir === "previous") {
      this.brandAnchor?.focus();
    }
  }

	/**
	 * @param {MouseEvent} ev 
	 */
  onClickCloseSearchButton(ev) {
		ev.preventDefault();
    isSearchActive.set(false);
    this.searchInput?.blur();
  }

  onBlurCloseSearchButton() {
    isSearchActive.set(false);

    if (this.#focusDir === "next") {
      if(this.isMobile){
        this.mobileNavButton?.focus();
      }else{
        this.siteBrandingName?.focus();
      }
    } else if (this.#focusDir === "previous") {
      this.brandAnchor?.focus();
    }
  }

  onClickToggleMobileNav() {
    isMobileActive.toggle();
  }

  onBlurToggleMobileNav() {
    if (this.#focusDir === "previous") {
      this.searchOpenButton?.focus();
    }  
  }

  onLogoFocus() {
    isSearchActive.set(false);
  }

  onSearchInputFocus() {
    isSearchActive.set(true);
  }

  /**
   * @param {KeyboardEvent} ev
   */
  onBodyMobileActiveKeyDown(ev) {
		if (!this.#isMobileActive) {
			return;
		}
		// TODO: Reproduce behavior of finding last result for 'bu-menu-item[active]' relative to mobileMenu and then calling `.focus()` on it.
    if (ev.code === "Escape") {
      this.mobileNavButton?.focus();
    }
  }

	/**
	 * Asynchronously loads the element from a centrally maintained template. Does NOT initialize it.
	 * Recommended that you call `init()` after this Promise completes.
	 * 
	 * @param {string?} [url=null] 
	 * @returns {Promise<void>}
	 */
	async load(url = null) {
		url = url ? url : templateUrl;
		try {
			this.#target = await loadRemoteElement({
				type: "Header",
				root: true,
				target: this.target,
				url,
				templateId: "bu-header",
			});
		} catch (err) {
			console.error(err);
		}
	}

	/**
	 * Initializes the element's state & attaches any necessary event handlers.
	 * 
	 * @returns {void}
	 */
	init() {
		// Get target element.
		const element = this.target;
		if (!element) {
			this.#logger.warn("No target found!");
			return;
		}

		// Setup element bindings.

		/** @type {HTMLElement?} */
		const siteBranding = document.getElementById("block-sitebranding");

		/** @type {HTMLImageElement?} */
		const logoImg = siteBranding?.querySelector(":scope img") ?? null;

		this.headerElement = this.target?.querySelector(":scope :is(header,.header)") ?? null;
		this.brandAnchor = siteBranding?.querySelector(":scope a") ?? null;
		this.searchInput = /** @type {HTMLInputElement} */ (document.getElementById("edit-keys"));
		this.searchOpenButton = /** @type {HTMLButtonElement} */ (document.getElementById("uiHeader_search"));
		this.searchCloseButton = /** @type {HTMLButtonElement} */ (document.getElementById("uiHeader-close-icon"));

		this.primaryNav = document.querySelector("nav.main.primary");
		this.mobileMenu = document.querySelector("nav.main.mobile");
		this.menu11 = /** @type {HTMLInputElement} */ (document.getElementById("menu-11"));
		this.mobileNavButton = /** @type {HTMLButtonElement} */ (document.getElementById("mobile-nav-button"));
		this.siteBrandingName = /** @type {HTMLAnchorElement} */ (document.getElementById("block-sitename")?.querySelector(":scope .site-branding-name a"));
		this.skipToNav = /** @type {HTMLAnchorElement} */ (document.getElementById("skipToNav"));

		// Initialize dynamic data & register events.

		if (logoImg) {
			if (!logoImg?.src) {
				logoImg.src = logoUrl;
			}
			logoImg.addEventListener("focus", () => {
				isSearchActive.set(false);
			});
		}

		this.searchInput?.addEventListener("focus", this.onSearchInputFocus.bind(this));
		this.searchOpenButton?.addEventListener("click", this.onClickOpenSearchButton.bind(this));
		this.searchOpenButton?.addEventListener("blur", this.onBlurOpenSearchButton.bind(this));
		this.searchCloseButton?.addEventListener("click", this.onClickCloseSearchButton.bind(this));
		this.searchCloseButton?.addEventListener("blur", this.onBlurCloseSearchButton.bind(this));
		this.mobileNavButton?.addEventListener("click", this.onClickToggleMobileNav.bind(this));
		this.mobileNavButton?.addEventListener("blur", this.onBlurToggleMobileNav.bind(this));

		this.windowWidth = window.innerWidth;
		window.addEventListener("resize", () => {
			this.windowWidth = window.innerWidth;
		});

		// Subscribe to stores.

		this.#noSearchStore = this.target ? new ReactiveClassStore(this.target, "no-search") : null;
		this.#noSearchStore?.subscribe(value => this.isNoSearch = value);

		isSearchActive.subscribe(value => this.#isSearchActive = value);
		isMobileActive.subscribe(value => this.#isMobileActive = value);
		focusDir.subscribe(value => this.#focusDir = /** @type {import("../public").DirectionAxis1} */ (value));

		isSearchActive.subscribe(value => {
			this.searchOpenButton?.classList.toggle("hidden", value);
			this.searchCloseButton?.classList.toggle("hidden", !value);
			this.headerElement?.classList.toggle("search-active", value);
		});

		if (this.siteBrandingName) {
			const siteName = this.target?.dataset.buSiteName;
			if (siteName) {
				this.siteBrandingName.textContent = siteName;
				this.siteBrandingName.ariaLabel = siteName;
			}
			const siteHref = this.target?.dataset.buSiteHref;
			if (siteHref) {
				this.siteBrandingName.href = siteHref;
			}
		}

		this.#updateMobileNavButton();
	}

	#updateMobileNavButton() {
		if (this.primaryNav && this.primaryNav.querySelector(":scope li")) {
			if (this.menu11) {
				this.menu11.style.display = "";
			}
			if (this.mobileNavButton) {
				this.mobileNavButton.style.display = "";
			}
		} else {
			if (this.menu11) {
				this.menu11.style.display = "none";
			}
			if (this.mobileNavButton) {
				this.mobileNavButton.style.display = "none";
			}
		}
	}
}

export default Header;